/* eslint-disable import/no-named-as-default-member */
import React, { Fragment, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Metrics from '@spotahome/soyuz-metrics';
import { Mediator } from '@spotahome/soyuz-mediator';
import { trans } from '@spotahome/soyuz/client';
import { Button, useModalHandle } from '@spotahome/ui-library';

import { addAlert } from '@components/AlertModal/mutations';
import AlertModalWrapper from '@components/AlertModal/index';

import { showSuccessToast } from '@alerts/components/AlertToast';

import { OPEN_ALERT } from '@common/channels';
import SoyuzLocalStorage from '@utils/soyuzLocalStorage';

const LOCAL_STORAGE_ALERT_DATA_KEY = 'alertData';

const parseBudget = (budget = '') => {
  const splitBudget = budget.split('-');

  return {
    minPrice: splitBudget[0],
    maxPrice: splitBudget[1]
  };
};

const getAlertFromFilters = (cityId, filters) => ({
  cityId,
  moveIn: filters['move-in'],
  moveOut: filters['move-out'],
  propertyTypes: filters['type[]'],
  cityAreaIds: filters['areaId[]'],
  ...parseBudget(filters.budget)
});

const getAlertDataFromStorage = () => {
  const data = SoyuzLocalStorage.extractItem(LOCAL_STORAGE_ALERT_DATA_KEY);
  try {
    return JSON.parse(data);
  } catch (e) {
    return null;
  }
};

const createAlert = async alertData => {
  try {
    await addAlert(alertData);
    Metrics.ga.sendEvent(
      'My alerts modal',
      'alert creation success',
      'alert creation success'
    );
    showSuccessToast();
    SoyuzLocalStorage.removeItem(LOCAL_STORAGE_ALERT_DATA_KEY);
  } catch (error) {
    console.error(error);
  }
};

const AlertButton = React.memo(
  ({ expandedFilters, cityId, filters, isUserLogged, allCities, onClick }) => {
    const { isModalOpen, handleModalOpen, handleModalClose } = useModalHandle();

    useEffect(() => {
      const alertData = getAlertDataFromStorage();
      if (alertData && isUserLogged) {
        createAlert(alertData);
      }
    }, [isUserLogged]);

    const getInitialAlertValues = () => getAlertFromFilters(cityId, filters);

    const handleOpen = () => {
      handleModalOpen();
      onClick();
    };

    const handleConfirm = async alertData => {
      Metrics.ga.sendEvent(
        'My alerts modal',
        'create alert button',
        'click button'
      );

      if (isUserLogged) {
        await createAlert(alertData);
      } else {
        SoyuzLocalStorage.setItem(
          LOCAL_STORAGE_ALERT_DATA_KEY,
          JSON.stringify(alertData)
        );
        Mediator.publish(OPEN_ALERT);
      }
      handleModalClose();
    };

    const alertResultButtonClasses = classNames(
      'filter-header__results-button',
      'ga-search-form-my-alerts',
      {
        'filter-header__results-button--filters-collapsed': !expandedFilters
      }
    );

    const renderAlertsButton = () => (
      <Button
        data-test="alert-button"
        onClick={handleOpen}
        alt
        className={alertResultButtonClasses}
      >
        {trans('search.alert.button.save-my-search')}
      </Button>
    );

    return (
      <Fragment>
        {renderAlertsButton()}

        <AlertModalWrapper
          alert={getInitialAlertValues(cityId, filters)}
          cities={allCities}
          onConfirm={handleConfirm}
          onClose={handleModalClose}
          isModalOpen={isModalOpen}
        />
      </Fragment>
    );
  }
);

AlertButton.propTypes = {
  cityId: PropTypes.string.isRequired,
  filters: PropTypes.shape({
    'bathrooms[]': PropTypes.arrayOf(PropTypes.string),
    bed: PropTypes.string,
    budget: PropTypes.string,
    'features[]': PropTypes.arrayOf(PropTypes.string),
    'move-in': PropTypes.string,
    'move-out': PropTypes.string,
    poi: PropTypes.string,
    'topology[]': PropTypes.arrayOf(PropTypes.string),
    'type[]': PropTypes.arrayOf(PropTypes.string)
  }).isRequired,
  allCities: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      currencyIsoCode: PropTypes.string
    })
  ).isRequired,
  isUserLogged: PropTypes.bool,
  expandedFilters: PropTypes.bool,
  onClick: PropTypes.func
};

AlertButton.defaultProps = {
  isUserLogged: false,
  expandedFilters: false,
  onClick: () => {}
};

export default AlertButton;
